import React, { useState, useEffect, useRef } from 'react';
import { Header, Footer } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Send, MapPin, Phone, FileText, CheckCircle, Users, ArrowRight,
  Shield, Heart, ClipboardList, Loader2, AlertCircle, ChevronDown
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '../utils/api';


// CESFAM data extracted from Excel
var CESFAM_DATA = [
  {
    name: "COSAM Puente Alto",
    address: "Arturo Prat #35, Puente Alto",
    lat: -33.6117, lng: -70.5758,
    program: "Ambulatorio Intensivo (PAI) + Programa ELUN (menores de 20)",
    contact: "Daniela Cabello / José Miguel Contreras",
    org: "Corporación de Salud Puente Alto",
    type: "cosam"
  },
  {
    name: "CESFAM Laurita Vicuña",
    address: "Av. Ejército Libertador 2433",
    lat: -33.5980, lng: -70.5810,
    program: "Salud Mental - Programa Apoyo Salud Mental",
    contact: "Marcelo Rodriguez, Carla Avila",
    org: "Corporación de Salud Puente Alto",
    type: "cesfam"
  },
  {
    name: "CESFAM Cardenal Raúl Silva Henríquez",
    address: "Estación El Canelo #3345",
    lat: -33.6280, lng: -70.5330,
    program: "Ambulatorio Básico para mayores de 18 años",
    contact: "Antoine Gárnica",
    org: "Corporación de Salud Puente Alto",
    type: "cesfam"
  },
  {
    name: "CESFAM San Gerónimo",
    address: "San Pedro 1203",
    lat: -33.6140, lng: -70.5690,
    program: "Ambulatorio Básico para mayores de 18 años",
    contact: "Sandra Galvéz",
    org: "Corporación de Salud Puente Alto",
    type: "cesfam"
  },
  {
    name: "CESFAM Padre Manuel Villaseca",
    address: "Luis Matte Larraín 2312",
    lat: -33.6050, lng: -70.5950,
    program: "Ambulatorio Básico para mayores de 18 años",
    contact: "Laura Leiva",
    org: "Corporación de Salud Puente Alto",
    type: "cesfam"
  },
  {
    name: "CESFAM Vista Hermosa",
    address: "El Volcán 4549",
    lat: -33.5890, lng: -70.5470,
    program: "Salud Mental - Programa Apoyo Salud Mental",
    contact: "Vanessa Silva",
    org: "Corporación de Salud Puente Alto",
    type: "cesfam"
  },
  {
    name: "CESFAM Bernardo Leighton",
    address: "Miguel Ángel N°1929",
    lat: -33.6190, lng: -70.5870,
    program: "Salud Mental - Programa Apoyo Salud Mental",
    contact: "Susana Alvarado",
    org: "Corporación de Salud Puente Alto",
    type: "cesfam"
  },
  {
    name: "CESFAM Karol Wojtyla",
    address: "Curaco de Vélez 4110",
    lat: -33.5830, lng: -70.5550,
    program: "Ambulatorio Intensivo para mayores de 18 años",
    contact: "Claudia Olivares",
    org: "Corporación de Salud Puente Alto",
    type: "cesfam"
  },
  {
    name: "CESFAM Alejandro del Río",
    address: "Calle Gandarillas N°105",
    lat: -33.6100, lng: -70.5760,
    program: "Salud Mental - Programa Apoyo Salud Mental",
    contact: "Susana Pino",
    org: "Fundación Áncora UC",
    type: "cesfam"
  },
  {
    name: "CESFAM Madre Teresa de Calcuta",
    address: "Circunvalación 1815",
    lat: -33.5950, lng: -70.5620,
    program: "Salud Mental - Participación Social",
    contact: "Marlen Garay",
    org: "Fundación Áncora UC",
    type: "cesfam"
  },
  {
    name: "CESFAM Alberto Hurtado",
    address: "Av. San Carlos 1693",
    lat: -33.6020, lng: -70.5730,
    program: "Salud Mental - Programa Salud Mental",
    contact: "Eddie Soto, Josefina Riesco",
    org: "Corporación Esperanza",
    type: "cesfam"
  },
  {
    name: "Fundación León Bloy - Centro Kantú",
    address: "José Manuel Irarrázaval #0575, Puente Alto",
    lat: -33.6130, lng: -70.5780,
    program: "Ambulatorio Intensivo para hombres mayores de 18 años",
    contact: "Fundación León Bloy",
    org: "Fundación León Bloy",
    type: "centro"
  }
];

var CESFAM_NAMES = CESFAM_DATA.filter(function(c) { return c.type === 'cesfam'; }).map(function(c) { return c.name; });

// Steps data
var STEPS = [
  { icon: ClipboardList, title: "Acogida", desc: "Recepción de la demanda espontánea o derivación del usuario" },
  { icon: Users, title: "Entrevista y Tamizaje", desc: "Diagnóstico preliminar mediante entrevista profesional" },
  { icon: Heart, title: "Charla Motivacional", desc: "Problematización del consumo y motivación al cambio" },
  { icon: Send, title: "Derivación a Red de Salud", desc: "Derivación asistida al CESFAM o centro más apropiado" },
  { icon: Shield, title: "Monitoreo", desc: "Seguimiento hasta el ingreso efectivo al tratamiento" }
];

function CesfamMap() {
  var mapRef = useRef(null);
  var mapInstance = useRef(null);

  useEffect(function() {
    if (mapInstance.current || !mapRef.current) return;

    // Dynamically import leaflet
    var L = require('leaflet');

    // Fix default icon issue
    delete L.Icon.Default.prototype._getIconUrl;
    L.Icon.Default.mergeOptions({
      iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
      iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
      shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
    });

    var map = L.map(mapRef.current).setView([-33.607, -70.572], 13);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© OpenStreetMap'
    }).addTo(map);

    // Custom icons
    var cesfamIcon = L.divIcon({
      className: 'custom-marker',
      html: '<div style="background:#10b981;color:white;width:32px;height:32px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:14px;font-weight:bold;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.3);">+</div>',
      iconSize: [32, 32],
      iconAnchor: [16, 16]
    });

    var cosamIcon = L.divIcon({
      className: 'custom-marker',
      html: '<div style="background:#8b5cf6;color:white;width:36px;height:36px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:16px;font-weight:bold;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.3);">C</div>',
      iconSize: [36, 36],
      iconAnchor: [18, 18]
    });

    var centroIcon = L.divIcon({
      className: 'custom-marker',
      html: '<div style="background:#f59e0b;color:white;width:32px;height:32px;border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:14px;font-weight:bold;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.3);">F</div>',
      iconSize: [32, 32],
      iconAnchor: [16, 16]
    });

    CESFAM_DATA.forEach(function(cesfam) {
      var icon = cesfam.type === 'cosam' ? cosamIcon : cesfam.type === 'centro' ? centroIcon : cesfamIcon;
      var typeLabel = cesfam.type === 'cosam' ? 'COSAM' : cesfam.type === 'centro' ? 'Centro de Tratamiento' : 'CESFAM';
      var typeColor = cesfam.type === 'cosam' ? '#8b5cf6' : cesfam.type === 'centro' ? '#f59e0b' : '#10b981';

      L.marker([cesfam.lat, cesfam.lng], { icon: icon })
        .addTo(map)
        .bindPopup(
          '<div style="font-family:sans-serif;min-width:220px;">' +
          '<div style="background:' + typeColor + ';color:white;padding:6px 10px;margin:-8px -20px 10px;font-size:11px;font-weight:600;letter-spacing:0.5px;">' + typeLabel + '</div>' +
          '<h3 style="margin:0 0 6px;font-size:14px;color:#1e293b;">' + cesfam.name + '</h3>' +
          '<p style="margin:0 0 4px;font-size:12px;color:#64748b;">' + cesfam.address + '</p>' +
          '<p style="margin:0 0 4px;font-size:12px;color:#0f766e;font-weight:500;">' + cesfam.program + '</p>' +
          '<p style="margin:0 0 4px;font-size:11px;color:#64748b;">Contacto: ' + cesfam.contact + '</p>' +
          '<p style="margin:0;font-size:11px;color:#94a3b8;">' + cesfam.org + '</p>' +
          '</div>',
          { maxWidth: 280 }
        );
    });

    mapInstance.current = map;

    return function() {
      if (mapInstance.current) {
        mapInstance.current.remove();
        mapInstance.current = null;
      }
    };
  }, []);

  return (
    <div>
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css" />
      <div ref={mapRef} style={{ height: '500px', width: '100%', borderRadius: '12px' }} data-testid="cesfam-map" />
      <div className="flex flex-wrap gap-4 mt-4 justify-center">
        <div className="flex items-center gap-2 text-sm text-slate-400">
          <div className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center text-white text-xs font-bold">+</div>
          CESFAM
        </div>
        <div className="flex items-center gap-2 text-sm text-slate-400">
          <div className="w-5 h-5 rounded-full bg-purple-500 flex items-center justify-center text-white text-xs font-bold">C</div>
          COSAM
        </div>
        <div className="flex items-center gap-2 text-sm text-slate-400">
          <div className="w-5 h-5 rounded-full bg-amber-500 flex items-center justify-center text-white text-xs font-bold">F</div>
          Centro de Tratamiento
        </div>
      </div>
    </div>
  );
}

export default function SendaPuenteAltoPage() {
  var [form, setForm] = useState({
    nombre: '', run: '', edad: '', direccion: '',
    cesfam: '', contacto: '', descripcion: ''
  });
  var [sending, setSending] = useState(false);
  var [sent, setSent] = useState(false);

  function handleChange(field, value) {
    setForm(function(prev) { return Object.assign({}, prev, { [field]: value }); });
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.nombre || !form.run || !form.edad || !form.direccion || !form.cesfam || !form.contacto || !form.descripcion) {
      toast.error('Por favor completa todos los campos');
      return;
    }
    setSending(true);
    try {
      await axios.post(API + '/senda-puente-alto/derivar', form);
      setSent(true);
      toast.success('Derivación enviada exitosamente');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al enviar. Intenta nuevamente.');
    }
    setSending(false);
  }

  return (
    <div className="min-h-screen bg-slate-950">
      
        <title>TTO Senda Puente Alto - Derivación y Tratamiento | SinAdicciones.org</title>
        <meta name="description" content="Formulario de derivación SENDA Previene Puente Alto. Acogida, entrevista, diagnóstico y derivación a la red de salud para tratamiento de adicciones." />
        <link rel="canonical" href="https://sinadicciones.org/senda-puente-alto" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sinadicciones.org/senda-puente-alto" />
        <meta property="og:title" content="TTO Senda Puente Alto - Derivación y Tratamiento" />
        <meta property="og:description" content="Formulario de derivación SENDA Previene Puente Alto. Acogida, entrevista, diagnóstico y derivación a la red de salud." />
        <meta property="og:image" content="https://sinadicciones.org/senda-previene-puente-alto.png" />
        <meta property="og:image:width" content="1024" />
        <meta property="og:image:height" content="896" />
        <meta property="og:site_name" content="SinAdicciones.org" />
        <meta property="og:locale" content="es_CL" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="TTO Senda Puente Alto - Derivación" />
        <meta name="twitter:description" content="Derivación SENDA Previene Puente Alto." />
        <meta name="twitter:image" content="https://sinadicciones.org/senda-previene-puente-alto.png" />
      

      <Header />

      <main className="pt-20">
        {/* Hero */}
        <section className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-900/40 via-slate-950 to-emerald-900/20" />
          <div className="relative max-w-5xl mx-auto px-4 md:px-8 py-16 md:py-24">
            <div className="flex flex-col md:flex-row items-center gap-8">
              <div className="flex-1 text-center md:text-left">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-500/10 border border-blue-500/20 rounded-full text-blue-400 text-sm font-medium mb-6">
                  <Shield className="w-4 h-4" />
                  SENDA Previene - Puente Alto
                </div>
                <h1 className="text-4xl sm:text-5xl font-bold text-slate-50 mb-4 leading-tight" data-testid="senda-hero-title">
                  TTO Senda<br />
                  <span className="text-emerald-400">Puente Alto</span>
                </h1>
                <p className="text-slate-400 text-lg mb-8 max-w-xl">
                  Programa de prevención y derivación de consumo de drogas. 
                  Realizamos acogida, entrevista, diagnóstico preliminar y derivación 
                  a la red de salud más apropiada para tu proceso de recuperación.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center md:justify-start">
                  <Button
                    onClick={function() { document.getElementById('formulario').scrollIntoView({ behavior: 'smooth' }); }}
                    size="lg"
                    className="bg-emerald-500 hover:bg-emerald-600 text-white px-8"
                    data-testid="senda-cta-derivar"
                  >
                    <Send className="w-5 h-5 mr-2" />
                    Derivar Paciente
                  </Button>
                  <Button
                    onClick={function() { document.getElementById('mapa').scrollIntoView({ behavior: 'smooth' }); }}
                    size="lg"
                    variant="outline"
                    className="border-slate-700 text-slate-300 hover:bg-slate-800"
                  >
                    <MapPin className="w-5 h-5 mr-2" />
                    Ver Mapa CESFAM
                  </Button>
                </div>
                {/* Mobile logo */}
                <div className="flex justify-center mt-6 md:hidden">
                  <a href="https://www.instagram.com/senda_puentealto/" target="_blank" rel="noopener noreferrer">
                    <img src="/senda-previene-puente-alto.png" alt="SENDA Previene Puente Alto" className="w-40 h-40 rounded-2xl object-contain" />
                  </a>
                </div>
              </div>
              <div className="flex-shrink-0 hidden md:block">
                <a href="https://www.instagram.com/senda_puentealto/" target="_blank" rel="noopener noreferrer" className="block hover:opacity-90 transition-opacity">
                  <img src="/senda-previene-puente-alto.png" alt="SENDA Previene Puente Alto" className="w-64 h-64 rounded-3xl object-contain" />
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="py-16 bg-slate-900/50">
          <div className="max-w-5xl mx-auto px-4 md:px-8">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-50 text-center mb-4">¿Cómo funciona el proceso?</h2>
            <p className="text-slate-400 text-center mb-12 max-w-2xl mx-auto">
              SENDA Previene no realiza tratamiento directamente. Nuestra gestión se enfoca en acoger, evaluar y derivar a la red de salud más apropiada.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {STEPS.map(function(step, i) {
                var Icon = step.icon;
                return (
                  <div key={i} className="relative">
                    <Card className="bg-slate-900 border-slate-800 h-full hover:border-emerald-500/30 transition-colors">
                      <CardContent className="p-5 text-center">
                        <div className="w-12 h-12 rounded-xl bg-emerald-500/10 flex items-center justify-center mx-auto mb-3">
                          <Icon className="w-6 h-6 text-emerald-400" />
                        </div>
                        <div className="text-emerald-400 text-xs font-bold mb-2">PASO {i + 1}</div>
                        <h3 className="text-slate-50 font-semibold text-sm mb-2">{step.title}</h3>
                        <p className="text-slate-500 text-xs leading-relaxed">{step.desc}</p>
                      </CardContent>
                    </Card>
                    {i < STEPS.length - 1 && (
                      <div className="hidden lg:flex absolute top-1/2 -right-2 transform -translate-y-1/2 z-10">
                        <ArrowRight className="w-4 h-4 text-emerald-500/40" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Requirements */}
        <section className="py-16">
          <div className="max-w-5xl mx-auto px-4 md:px-8">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-50 text-center mb-10">Requisitos de Ingreso</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-3xl mx-auto">
              {[
                { text: "Domicilio en la Comuna de Puente Alto", icon: MapPin },
                { text: "Estar inscrito en CESFAM de la comuna", icon: FileText },
                { text: "Pertenecer a FONASA o PRAIS", icon: Shield },
                { text: "Voluntad de iniciar proceso de recuperación", icon: Heart }
              ].map(function(req, i) {
                var Icon = req.icon;
                return (
                  <div key={i} className="flex items-center gap-4 p-4 bg-slate-900 border border-slate-800 rounded-xl">
                    <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-emerald-400" />
                    </div>
                    <p className="text-slate-300 text-sm">{req.text}</p>
                  </div>
                );
              })}
            </div>
            <div className="mt-6 p-4 bg-blue-500/5 border border-blue-500/20 rounded-xl max-w-3xl mx-auto">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                <p className="text-slate-400 text-sm">
                  <strong className="text-blue-400">Importante:</strong> El tratamiento es de carácter voluntario. 
                  La persona debe problematizar su consumo de drogas y manifestar querer iniciar un proceso de recuperación.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Derivation Form */}
        <section id="formulario" className="py-16 bg-slate-900/50">
          <div className="max-w-2xl mx-auto px-4 md:px-8">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-50 text-center mb-3">Formulario de Derivación</h2>
            <p className="text-slate-400 text-center mb-8">
              Completa los datos para enviar la solicitud de derivación a SENDA Previene Puente Alto
            </p>

            {sent ? (
              <Card className="bg-slate-900 border-emerald-500/30">
                <CardContent className="p-8 text-center">
                  <div className="w-20 h-20 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-10 h-10 text-emerald-400" />
                  </div>
                  <h3 className="text-2xl font-bold text-slate-50 mb-3" data-testid="senda-form-success">Derivación Enviada</h3>
                  <p className="text-slate-400 mb-6">
                    El equipo de SENDA Previene Puente Alto recibirá la información y tomará contacto con el usuario para agendar una entrevista.
                  </p>
                  <Button onClick={function() { setSent(false); setForm({ nombre: '', run: '', edad: '', direccion: '', cesfam: '', contacto: '', descripcion: '' }); }} className="bg-emerald-500 hover:bg-emerald-600">
                    Enviar otra derivación
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-slate-900 border-slate-800">
                <CardContent className="p-6 md:p-8">
                  <form onSubmit={handleSubmit} className="space-y-5" data-testid="senda-derivation-form">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-300 mb-2 block">Nombre usuario/a *</Label>
                        <Input
                          value={form.nombre}
                          onChange={function(e) { handleChange('nombre', e.target.value); }}
                          placeholder="Nombre completo"
                          className="bg-slate-800 border-slate-700 text-slate-50"
                          data-testid="senda-input-nombre"
                          required
                        />
                      </div>
                      <div>
                        <Label className="text-slate-300 mb-2 block">RUN *</Label>
                        <Input
                          value={form.run}
                          onChange={function(e) { handleChange('run', e.target.value); }}
                          placeholder="12.345.678-9"
                          className="bg-slate-800 border-slate-700 text-slate-50"
                          data-testid="senda-input-run"
                          required
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-300 mb-2 block">Edad *</Label>
                        <Input
                          value={form.edad}
                          onChange={function(e) { handleChange('edad', e.target.value); }}
                          placeholder="Ej: 32"
                          type="number"
                          className="bg-slate-800 border-slate-700 text-slate-50"
                          data-testid="senda-input-edad"
                          required
                        />
                      </div>
                      <div>
                        <Label className="text-slate-300 mb-2 block">Medio de contacto *</Label>
                        <Input
                          value={form.contacto}
                          onChange={function(e) { handleChange('contacto', e.target.value); }}
                          placeholder="Teléfono o email"
                          className="bg-slate-800 border-slate-700 text-slate-50"
                          data-testid="senda-input-contacto"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <Label className="text-slate-300 mb-2 block">Dirección *</Label>
                      <Input
                        value={form.direccion}
                        onChange={function(e) { handleChange('direccion', e.target.value); }}
                        placeholder="Dirección completa en Puente Alto"
                        className="bg-slate-800 border-slate-700 text-slate-50"
                        data-testid="senda-input-direccion"
                        required
                      />
                    </div>

                    <div>
                      <Label className="text-slate-300 mb-2 block">CESFAM donde esté inscrito *</Label>
                      <Select value={form.cesfam} onValueChange={function(v) { handleChange('cesfam', v); }}>
                        <SelectTrigger className="bg-slate-800 border-slate-700 text-slate-300" data-testid="senda-select-cesfam">
                          <SelectValue placeholder="Selecciona el CESFAM" />
                        </SelectTrigger>
                        <SelectContent className="bg-slate-800 border-slate-700">
                          {CESFAM_NAMES.map(function(name) {
                            return <SelectItem key={name} value={name} className="text-slate-300">{name}</SelectItem>;
                          })}
                        </SelectContent>
                      </Select>
                      <p className="text-slate-500 text-xs mt-1">Debe ser un CESFAM de la comuna de Puente Alto</p>
                    </div>

                    <div>
                      <Label className="text-slate-300 mb-2 block">Breve descripción del caso *</Label>
                      <Textarea
                        value={form.descripcion}
                        onChange={function(e) { handleChange('descripcion', e.target.value); }}
                        placeholder="Describe brevemente la situación del usuario..."
                        rows={4}
                        className="bg-slate-800 border-slate-700 text-slate-50 resize-none"
                        data-testid="senda-input-descripcion"
                        required
                      />
                    </div>

                    <Button
                      type="submit"
                      disabled={sending}
                      className="w-full bg-emerald-500 hover:bg-emerald-600 text-white py-6 text-base"
                      data-testid="senda-submit-btn"
                    >
                      {sending ? (
                        <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Enviando derivación...</>
                      ) : (
                        <><Send className="w-5 h-5 mr-2" />Enviar Derivación a SENDA</>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            )}
          </div>
        </section>

        {/* Map */}
        <section id="mapa" className="py-16">
          <div className="max-w-5xl mx-auto px-4 md:px-8">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-50 text-center mb-3">Red de Salud - CESFAM Puente Alto</h2>
            <p className="text-slate-400 text-center mb-8">
              Haz clic en cada marcador para ver información detallada del centro
            </p>
            <CesfamMap />
          </div>
        </section>

        {/* CESFAM Directory */}
        <section className="py-16 bg-slate-900/50">
          <div className="max-w-5xl mx-auto px-4 md:px-8">
            <h2 className="text-2xl font-bold text-slate-50 text-center mb-8">Directorio de Centros de Salud</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {CESFAM_DATA.map(function(cesfam, i) {
                var typeColor = cesfam.type === 'cosam' ? 'purple' : cesfam.type === 'centro' ? 'amber' : 'emerald';
                var typeLabel = cesfam.type === 'cosam' ? 'COSAM' : cesfam.type === 'centro' ? 'Centro' : 'CESFAM';
                return (
                  <Card key={i} className="bg-slate-900 border-slate-800 hover:border-slate-700 transition-colors">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={'px-2 py-0.5 text-xs font-semibold rounded-full bg-' + typeColor + '-500/20 text-' + typeColor + '-400'}>
                          {typeLabel}
                        </span>
                      </div>
                      <h3 className="text-slate-50 font-semibold text-sm mb-1">{cesfam.name}</h3>
                      <p className="text-slate-500 text-xs flex items-center gap-1 mb-2">
                        <MapPin className="w-3 h-3" />{cesfam.address}
                      </p>
                      <p className="text-emerald-400/80 text-xs mb-1">{cesfam.program}</p>
                      <p className="text-slate-500 text-xs">Contacto: {cesfam.contact}</p>
                      <p className="text-slate-600 text-xs mt-1">{cesfam.org}</p>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* Info section */}
        <section className="py-16">
          <div className="max-w-3xl mx-auto px-4 md:px-8">
            <Card className="bg-slate-900 border-slate-800">
              <CardContent className="p-6 md:p-8">
                <h2 className="text-xl font-bold text-slate-50 mb-4">Información Importante</h2>
                <div className="space-y-4 text-sm text-slate-400 leading-relaxed">
                  <p>
                    <strong className="text-slate-300">SENDA Previene</strong> no realiza tratamiento propiamente tal. 
                    Nuestra gestión se enfoca en acoger demandas espontáneas y/o derivaciones de usuarios, 
                    para realizar primera acogida, entrevista y diagnóstico preliminar por medio de tamizaje, 
                    además de charla motivacional y problematización del consumo.
                  </p>
                  <p>
                    Posteriormente se realiza la <strong className="text-slate-300">derivación asistida a la red de salud</strong> más apropiada, 
                    donde los usuarios recibirán su real proceso de recuperación.
                  </p>
                  <p>
                    Una vez recepcionada la derivación, se toma contacto con el usuario, se cita a entrevista 
                    y luego se realiza derivación asistida a la red de salud, <strong className="text-slate-300">monitoreando hasta su ingreso efectivo</strong>. 
                    Según el diagnóstico que realiza la red de salud, se determina si el usuario requiere 
                    una atención más especializada, internación en un centro, etc.
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
